'use client';

import { useMemo, useState } from 'react';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const SAMPLE_JSON = `{
  "name": "Cleanor",
  "active": true,
  "version": 3,
  "tags": ["storage", "cleanup", "privacy"],
  "meta": {
    "downloads": 128000,
    "rating": 4.8,
    "featured": null
  }
}`;

type ParseResult = { ok: true; value: unknown } | { ok: false; error: string };

function parseJson(text: string): ParseResult {
  if (!text.trim()) {
    return { ok: false, error: '' };
  }
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Invalid JSON.' };
  }
}

function valueColor(value: unknown): string {
  if (typeof value === 'string') {
    return '#16a34a';
  }
  if (typeof value === 'number') {
    return '#2563eb';
  }
  if (typeof value === 'boolean') {
    return '#c026d3';
  }
  return '#94a3b8';
}

function renderPrimitive(value: unknown): string {
  if (typeof value === 'string') {
    return `"${value}"`;
  }
  if (value === null) {
    return 'null';
  }
  return String(value);
}

function collectPaths(value: unknown, path: string, acc: Set<string>) {
  if (value !== null && typeof value === 'object') {
    acc.add(path);
    if (Array.isArray(value)) {
      value.forEach((item, index) => collectPaths(item, `${path}.${index}`, acc));
    } else {
      Object.entries(value as Record<string, unknown>).forEach(([key, item]) =>
        collectPaths(item, `${path}.${key}`, acc),
      );
    }
  }
}

type NodeProps = {
  label: string | null;
  value: unknown;
  path: string;
  expanded: Set<string>;
  toggle: (path: string) => void;
  depth: number;
};

function TreeNode({ label, value, path, expanded, toggle, depth }: NodeProps) {
  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);
  const isOpen = expanded.has(path);

  const labelNode =
    label !== null ? (
      <span style={{ color: '#0f766e' }}>{isArrayKey(label) ? label : `"${label}"`}: </span>
    ) : null;

  if (!isObject) {
    return (
      <div style={{ paddingLeft: `${depth * 1.1}rem`, lineHeight: 1.7 }}>
        {labelNode}
        <span style={{ color: valueColor(value) }}>{renderPrimitive(value)}</span>
      </div>
    );
  }

  const entries: [string, unknown][] = isArray
    ? (value as unknown[]).map((item, index) => [String(index), item])
    : Object.entries(value as Record<string, unknown>);

  const openBrace = isArray ? '[' : '{';
  const closeBrace = isArray ? ']' : '}';
  const summary = `${entries.length} ${entries.length === 1 ? 'item' : 'items'}`;

  return (
    <div style={{ paddingLeft: `${depth * 1.1}rem`, lineHeight: 1.7 }}>
      <button
        onClick={() => toggle(path)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          font: 'inherit',
          color: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.35rem',
        }}
        type="button"
      >
        <span style={{ opacity: 0.7, width: '0.9rem', display: 'inline-block' }}>
          {isOpen ? '▾' : '▸'}
        </span>
        {labelNode}
        <span style={{ opacity: 0.8 }}>
          {openBrace}
          {isOpen ? '' : ` ${summary} ${closeBrace}`}
        </span>
      </button>
      {isOpen ? (
        <>
          {entries.map(([key, item]) => (
            <TreeNode
              depth={depth + 1}
              expanded={expanded}
              key={key}
              label={isArray ? `${key}` : key}
              path={`${path}.${key}`}
              toggle={toggle}
              value={item}
            />
          ))}
          <div style={{ paddingLeft: `${depth * 1.1}rem`, opacity: 0.8 }}>{closeBrace}</div>
        </>
      ) : null}
    </div>
  );
}

function isArrayKey(label: string): boolean {
  return /^\d+$/.test(label);
}

export default function BrowserJsonTreeViewer() {
  const [input, setInput] = useState(SAMPLE_JSON);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['$']));
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const parsed = useMemo(() => parseJson(input), [input]);

  const allPaths = useMemo(() => {
    if (!parsed.ok) {
      return new Set<string>();
    }
    const acc = new Set<string>();
    collectPaths(parsed.value, '$', acc);
    return acc;
  }, [parsed]);

  function toggle(path: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }

  function expandAll() {
    setExpanded(new Set(allPaths));
  }

  function collapseAll() {
    setExpanded(new Set(['$']));
  }

  async function handleCopyFormatted() {
    if (!parsed.ok) {
      return;
    }
    try {
      await copyText(JSON.stringify(parsed.value, null, 2));
      setCopyMessage('Formatted JSON copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the JSON.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">JSON</span>
        <h2 className="tool-live-title">JSON tree viewer</h2>
        <p className="tool-live-subtitle">
          Paste JSON and explore it as a collapsible, color-coded tree. Expand or collapse branches,
          copy formatted output, and catch parse errors instantly, all in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>JSON input</span>
              <textarea
                onChange={(event) => setInput(event.target.value)}
                placeholder='{ "key": "value" }'
                rows={16}
                spellCheck={false}
                value={input}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!parsed.ok}
              onClick={expandAll}
              type="button"
            >
              Expand all
            </button>
            <button
              className="button button-secondary"
              disabled={!parsed.ok}
              onClick={collapseAll}
              type="button"
            >
              Collapse all
            </button>
            <button
              className="button button-secondary"
              disabled={!parsed.ok}
              onClick={handleCopyFormatted}
              type="button"
            >
              Copy formatted
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          {!parsed.ok && parsed.error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {parsed.error}
            </p>
          ) : null}
        </section>

        <section className="tool-live-preview">
          <div className="tool-live-field tool-live-field-full">
            <span>Tree</span>
            <div
              style={{
                fontFamily: 'var(--font-mono, monospace)',
                fontSize: '0.85rem',
                padding: '0.85rem',
                borderRadius: '0.6rem',
                border: '1px solid rgba(148, 163, 184, 0.35)',
                minHeight: '6rem',
                maxHeight: '28rem',
                overflow: 'auto',
              }}
            >
              {parsed.ok ? (
                <TreeNode
                  depth={0}
                  expanded={expanded}
                  label={null}
                  path="$"
                  toggle={toggle}
                  value={parsed.value}
                />
              ) : (
                <span style={{ opacity: 0.6 }}>
                  {parsed.error ? 'Fix the JSON to see the tree.' : 'Paste JSON to see the tree.'}
                </span>
              )}
            </div>
          </div>
          <p className="tool-live-panel-note">Parsed locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
